import { useEffect, useRef, useState } from "react";
import { Eraser, Save, X } from "lucide-react";

/**
 * Lightweight pointer-based signature pad.
 *
 * Renders a 600 × 300 logical canvas (CSS-sized) that scales for HiDPI
 * displays. Works on desktop (mouse) and touch screens (finger / stylus).
 * Calls `onSave(blob)` with a PNG `Blob` when the user clicks Save.
 *
 * Why a hand-rolled canvas instead of `react-signature-canvas`?
 *   - One extra dependency to maintain.
 *   - Pointer events + DPR scaling fit in <120 LOC.
 *   - We need a transparent PNG (matches the existing upload path) which
 *     `toBlob('image/png')` gives us for free.
 */
export default function SignaturePad({
  width = 600,
  height = 300,
  onSave,
  onCancel,
  busy = false,
}) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPtRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);

  // Initialise the canvas backing store at DPR scale so strokes stay sharp
  // on retina screens. Logical (CSS) size stays 600 × 300.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#1c1c1c";
    ctx.lineWidth = 2.2;
  }, [width, height]);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX ?? e.touches?.[0]?.clientX) - rect.left),
      y: ((e.clientY ?? e.touches?.[0]?.clientY) - rect.top),
    };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastPtRef.current = getPoint(e);
    // Try to capture pointer so we keep receiving moves even if the user
    // drags off the canvas — matches native ink behaviour.
    if (e.pointerId !== undefined) {
      try { canvasRef.current.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    }
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const pt = getPoint(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(lastPtRef.current.x, lastPtRef.current.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();
    lastPtRef.current = pt;
    if (isEmpty) setIsEmpty(false);
  };

  const end = (e) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (e?.pointerId !== undefined) {
      try { canvasRef.current.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    }
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    setIsEmpty(true);
  };

  const save = () => {
    if (isEmpty) return;
    canvasRef.current.toBlob((blob) => {
      if (blob) onSave?.(blob);
    }, "image/png");
  };

  return (
    <div className="space-y-3" data-testid="signature-pad">
      <div className="text-[11px] opacity-70">
        Draw your signature in the box below. Works on desktop (mouse) and mobile (finger / stylus).
        Captured at <b>{width} × {height}</b> px on a transparent background.
      </div>
      <div
        className="inline-block relative bg-white rounded"
        style={{
          width,
          maxWidth: "100%",
          border: "2px dashed var(--vb-border)",
          touchAction: "none",
        }}
      >
        <canvas
          ref={canvasRef}
          data-testid="signature-pad-canvas"
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
          onPointerLeave={end}
          style={{ display: "block", width: "100%", height, cursor: "crosshair" }}
        />
        {isEmpty && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="opacity-40 italic text-sm">Sign here</span>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" className="vb-btn sm" onClick={save} disabled={isEmpty || busy}
          data-testid="signature-pad-save">
          <Save size={12} /> Save signature
        </button>
        <button type="button" className="vb-btn ghost sm" onClick={clear} disabled={isEmpty || busy}
          data-testid="signature-pad-clear">
          <Eraser size={12} /> Clear
        </button>
        {onCancel && (
          <button type="button" className="vb-btn ghost sm" onClick={onCancel} disabled={busy}
            data-testid="signature-pad-cancel">
            <X size={12} /> Cancel
          </button>
        )}
      </div>
    </div>
  );
}
